import type { QueryClient } from '@tanstack/vue-query'
import type { MaybeRefOrGetter, Ref } from 'vue'
import type { ReceiptModel } from '~/utils/receipt-model/ReceiptModel'
import { getCurrentScope, onScopeDispose, readonly, ref, toValue } from 'vue'
import { queryKeys } from '~/queryClient'

/** Retry waits after the first attempt: the backend generates the letter asynchronously after the PUT. */
export const BILLING_LETTER_LINK_RETRY_DELAYS_MS: readonly number[] = [2000, 4000, 8000]

export interface UseBillingLetterLinkPollingOptions {
  fetchLink: () => Promise<string | null>
  retryDelaysMs?: readonly number[]
}

export interface UseBillingLetterLinkPollingResult {
  start: () => Promise<string | null>
  stop: () => void
  isPolling: Readonly<Ref<boolean>>
}

/** Timers are cleared by `stop()` and on scope dispose; `start()` cancels the previous run and never rejects. */
export function useBillingLetterLinkPolling(options: UseBillingLetterLinkPollingOptions): UseBillingLetterLinkPollingResult {
  const { fetchLink, retryDelaysMs = BILLING_LETTER_LINK_RETRY_DELAYS_MS } = options

  const isPolling = ref(false)
  // Each `start()` gets an id; a stopped (or superseded) run sees a different id and quits without touching state.
  let currentRun = 0
  let cancelWait: (() => void) | null = null

  function stop() {
    currentRun++
    cancelWait?.()
    cancelWait = null
    isPolling.value = false
  }

  function wait(ms: number): Promise<boolean> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        cancelWait = null
        resolve(true)
      }, ms)
      cancelWait = () => {
        clearTimeout(timer)
        resolve(false)
      }
    })
  }

  async function attempt(): Promise<string | null> {
    try {
      return (await fetchLink()) || null
    }
    catch (error) {
      console.error(error)
      return null
    }
  }

  async function start(): Promise<string | null> {
    stop()
    const run = currentRun
    const isCurrentRun = () => run === currentRun
    isPolling.value = true

    let link = await attempt()
    for (const delay of retryDelaysMs) {
      if (link || !isCurrentRun())
        break
      if (!(await wait(delay)))
        return null
      link = await attempt()
    }

    if (!isCurrentRun())
      return null

    isPolling.value = false
    return link
  }

  if (getCurrentScope())
    onScopeDispose(stop)

  return {
    start,
    stop,
    isPolling: readonly(isPolling),
  }
}

/**
 * Refetches the receipt GET and reads the link from the cache (props may be stale). Only counts for `supplierId` and a
 * file other than `previousLink`; the query string is ignored because the signed URL changes on every GET.
 */
export function createBillingLetterLinkFetcher(
  queryClient: QueryClient,
  projectId: MaybeRefOrGetter<string>,
  supplierId: MaybeRefOrGetter<number | null>,
  previousLink: MaybeRefOrGetter<string | null> = null,
): () => Promise<string | null> {
  return async () => {
    const queryKey = queryKeys.PAYMENT_MODEL.DATA(toValue(projectId))
    // `cancelRefetch: false` reuses a GET already in flight (e.g. the step's own refetch) instead of cancelling it.
    await queryClient.refetchQueries({ queryKey, exact: true }, { cancelRefetch: false })

    const supplier = queryClient.getQueryData<ReceiptModel>(queryKey)?.supplier
    const expectedSupplierId = toValue(supplierId)
    if (!supplier || expectedSupplierId === null || supplier.id !== expectedSupplierId)
      return null

    const link = supplier.billing_letter_link || null
    const before = toValue(previousLink)
    if (link && before && stripQuery(link) === stripQuery(before))
      return null

    return link
  }
}

function stripQuery(url: string): string {
  return url.split('?')[0]
}
